/**
 * Calibration — Week 4 of the HR framework cycle.
 *
 * Implements rules in CLAUDE.md §E:
 *   - Bell-curve targets and allowed ranges
 *   - Lock status per band (within / review / out)
 *   - Immutable audit row per adjustment
 *
 * One CalibrationSession per cycle. Adjustments live in their own collection
 * so the original submitted scores are never overwritten.
 */

import type { Timestamp } from 'firebase/firestore';

// ─── Bell-curve bands (§E.4) ────────────────────────────────────────────────

export type CalibrationBand =
  | 'exceptional'
  | 'exceed'
  | 'meet'
  | 'needs_improvement'
  | 'unsatisfactory';

export const CALIBRATION_BANDS: CalibrationBand[] = [
  'exceptional', 'exceed', 'meet', 'needs_improvement', 'unsatisfactory',
];

export const CALIBRATION_BAND_LABELS: Record<CalibrationBand, string> = {
  exceptional:       'Exceptional',
  exceed:            'Exceed',
  meet:              'Meet',
  needs_improvement: 'Needs Improvement',
  unsatisfactory:    'Unsatisfactory',
};

export interface BandRange {
  min: number;  // inclusive, percent (0-100)
  max: number;  // inclusive
}

export interface BandTargets {
  target: BandRange;
  allowed: BandRange;
}

/**
 * Canonical bell-curve targets per CLAUDE.md §E.4.
 * Do not change without HR sign-off (CLAUDE.md §I).
 */
export const BELL_CURVE_TARGETS: Record<CalibrationBand, BandTargets> = {
  exceptional:       { target: { min: 5,  max: 10 }, allowed: { min: 0,  max: 15 } },
  exceed:            { target: { min: 15, max: 20 }, allowed: { min: 10, max: 30 } },
  meet:              { target: { min: 50, max: 60 }, allowed: { min: 40, max: 70 } },
  needs_improvement: { target: { min: 10, max: 15 }, allowed: { min: 5,  max: 20 } },
  unsatisfactory:    { target: { min: 0,  max: 5  }, allowed: { min: 0,  max: 10 } },
};

// ─── Lock status per band (§E.5) ────────────────────────────────────────────

export type BandLockStatus = 'within' | 'review' | 'out';

export interface BandRow {
  count: number;
  pct: number;                  // current %, computed from count / total
  status: BandLockStatus;
  /** Required when status === 'review'. Must be empty for 'within' and 'out'. */
  justification?: string;
}

export type BandDistribution = Record<CalibrationBand, BandRow>;

// ─── Calibration session ────────────────────────────────────────────────────

export type CalibrationSessionStatus =
  | 'preparing'   // managers writing 2-3 sentence justifications (§E.2)
  | 'in_session'  // live session
  | 'locked';     // distribution locked, scores written back to EPRSubmissions

export interface CalibrationAttendees {
  managerIds: string[];
  hrIds: string[];
  mdId?: string;          // Managing Director per §E.1
}

/**
 * Pre-session preparation written by each manager for each of their direct
 * reports (§E.2). Stored on the session document keyed by employeeId.
 */
export interface ManagerPrep {
  employeeId: string;
  managerId: string;
  draftRating: CalibrationBand;
  justification: string;       // 2-3 sentence rationale, required
  flaggedForDiscussion: boolean;
  flagReason?: string;
}

export interface CalibrationSession {
  id: string;             // = cycleId
  cycleId: string;
  status: CalibrationSessionStatus;
  attendees: CalibrationAttendees;
  managerPreps: ManagerPrep[];
  distribution: BandDistribution;

  /** Snapshot of total employees being calibrated. */
  totalEmployees: number;

  lockedAt?: Timestamp;
  lockedBy?: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
}

// ─── Adjustment audit (§E.6) ────────────────────────────────────────────────

export type AdjustmentScope = 'item' | 'component' | 'total';

/**
 * Immutable audit row. Every score change during calibration produces a new
 * doc — never overwrite. Per CLAUDE.md §E.6.
 */
export interface CalibrationAdjustment {
  id: string;
  cycleId: string;
  employeeId: string;
  submissionId: string;   // FK to EPRSubmission

  scope: AdjustmentScope;
  /** Component key when scope='component'; item id when scope='item'; null for total. */
  componentOrItemId?: string;

  originalScore: number;
  adjustedScore: number;
  reason: string;         // required

  approvedBy: string;     // userId of approver
  adjustedAt: Timestamp;
}
